import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { LscMember, LscVodCandidate } from '../types';
import { createLscIdentityOverride, listLscMembers, LscApiError } from '../api';
import { useLeague } from '../hooks/useLeague';
import ErrorBanner from './ErrorBanner';
import {
  truthCommissionerIdentityOverrides,
  truthHasCommissionerIdentityOverrides,
  truthHudNormalizationStatusLabel,
  truthIdentityOverrideSummary,
  truthIsOverrideEligible,
  truthOverridableIdentityTags,
} from '../vodTruth';
import { nflTeamsMatch } from '../cfbBranding/teamVisualResolver';
import {
  LINK_GAMERTAG_PANEL_HELPER,
  LINK_GAMERTAG_PANEL_TITLE,
  UNRESOLVED_GAMERTAGS_LABEL,
} from '../commissionerReviewCopy';

interface CommissionerIdentityOverridePanelProps {
  leagueId: string;
  candidate: LscVodCandidate;
  onUpdated: () => Promise<void>;
}

function memberInGameName(member: LscMember): string {
  const identity = member.identities?.find((item) => item.identity_value)?.identity_value;
  if (identity) {
    return identity;
  }
  const meta = member.source_metadata;
  if (meta && typeof meta.in_game_name === 'string') {
    return meta.in_game_name;
  }
  return '';
}

function memberStatusLabel(member: LscMember): string {
  const status = String(member.status ?? 'active');
  if (status === 'imported_unclaimed') {
    return 'Pending Link';
  }
  if (status === 'active') {
    return 'Active';
  }
  return status.replace(/_/g, ' ');
}

function sidesMatch(memberSide: string | null | undefined, detectedSide: string): boolean {
  const member = String(memberSide ?? '').trim().toLowerCase();
  const detected = detectedSide.trim().toLowerCase();
  if (!member || !detected) {
    return false;
  }
  return (
    member === detected ||
    member.includes(detected) ||
    detected.includes(member) ||
    nflTeamsMatch(member, detected)
  );
}

function memberOptionLabel(member: LscMember): string {
  const inGame = memberInGameName(member);
  const side = String(member.current_side_label ?? '').trim();
  const parts = [member.display_name];
  if (inGame) {
    parts.push(inGame);
  }
  if (side) {
    parts.push(side);
  }
  parts.push(memberStatusLabel(member));
  return parts.join(' · ');
}

const CommissionerIdentityOverridePanel: React.FC<CommissionerIdentityOverridePanelProps> = ({
  leagueId,
  candidate,
  onUpdated,
}) => {
  const { isCommissioner } = useLeague();
  const [members, setMembers] = useState<LscMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedTagKey, setSelectedTagKey] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eligible = truthIsOverrideEligible(candidate);
  const overridableTags = truthOverridableIdentityTags(candidate);
  const existingOverrides = truthCommissionerIdentityOverrides(candidate);
  const overrideSummary = truthIdentityOverrideSummary(candidate);

  const selectedTag = useMemo(() => {
    if (!selectedTagKey) {
      return overridableTags[0] ?? null;
    }
    const [raw, side] = selectedTagKey.split('::');
    return overridableTags.find(
      (item) => String(item.raw_tag ?? '') === raw && String(item.detected_side ?? '') === side
    );
  }, [overridableTags, selectedTagKey]);

  const sameSideMembers = useMemo(() => {
    if (!selectedTag?.detected_side) {
      return [];
    }
    const detectedSide = String(selectedTag.detected_side);
    return members.filter((member) => {
      if (member.status === 'inactive' || member.deactivated_at) {
        return false;
      }
      if (member.merged_into_member_id) {
        return false;
      }
      return sidesMatch(member.current_side_label, detectedSide);
    });
  }, [members, selectedTag]);

  useEffect(() => {
    if (!isCommissioner || !eligible) {
      return;
    }
    let cancelled = false;
    setLoadingMembers(true);
    listLscMembers(leagueId)
      .then((result) => {
        if (!cancelled) {
          setMembers(result.members ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMembers([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingMembers(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eligible, isCommissioner, leagueId]);

  useEffect(() => {
    if (overridableTags.length === 0) {
      setSelectedTagKey('');
      return;
    }
    const first = overridableTags[0];
    setSelectedTagKey(`${String(first.raw_tag ?? '')}::${String(first.detected_side ?? '')}`);
  }, [overridableTags]);

  useEffect(() => {
    setSelectedMemberId('');
  }, [selectedTagKey]);

  if (!isCommissioner || !eligible) {
    return null;
  }

  const selectedMember = sameSideMembers.find((member) => member.id === selectedMemberId);

  const submitOverride = async () => {
    if (!selectedTag?.raw_tag || !selectedTag.detected_side || !selectedMemberId) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createLscIdentityOverride(leagueId, candidate.id, {
        raw_tag: String(selectedTag.raw_tag),
        detected_side: String(selectedTag.detected_side),
        member_id: selectedMemberId,
        note: note.trim() || undefined,
      });
      setConfirmOpen(false);
      setNote('');
      await onUpdated();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Identity override failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="sm:col-span-2 rounded-lg border border-amber-800/60 bg-amber-950/20 p-4 space-y-4"
      id="identity-override"
      data-testid="commissioner-identity-override-panel"
    >
      <div>
        <h3 className="text-sm font-semibold text-amber-100">{LINK_GAMERTAG_PANEL_TITLE}</h3>
        <p className="text-sm text-amber-200/80 mt-1">{LINK_GAMERTAG_PANEL_HELPER}</p>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {truthHasCommissionerIdentityOverrides(candidate) && overrideSummary && (
        <div
          className="rounded border border-green-800/60 bg-green-950/30 p-3 text-sm text-green-100 whitespace-pre-line"
          data-testid="commissioner-override-evidence"
        >
          {overrideSummary}
        </div>
      )}

      {existingOverrides.length > 0 && (
        <div className="text-sm text-secondary-300 space-y-1" data-testid="existing-overrides">
          {existingOverrides.map((item, index) => (
            <p key={`override-${index}`}>
              {item.raw_tag} → {item.member_display_name}
              {item.member_in_game_name ? ` · ${item.member_in_game_name}` : ''}
              {item.detected_side ? ` · ${item.detected_side}` : ''}
            </p>
          ))}
        </div>
      )}

      {overridableTags.length > 0 && (
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-secondary-500 mb-2">
              {UNRESOLVED_GAMERTAGS_LABEL}
            </p>
            <ul className="space-y-2" data-testid="overridable-hud-tags">
              {overridableTags.map((item, index) => {
                const key = `${String(item.raw_tag ?? '')}::${String(item.detected_side ?? '')}`;
                const statusLabel = truthHudNormalizationStatusLabel(item);
                return (
                  <li key={`tag-${index}`} className="text-sm text-secondary-200">
                    <label className="inline-flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="override-tag"
                        checked={selectedTagKey === key}
                        onChange={() => setSelectedTagKey(key)}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium text-white">{item.raw_tag ?? 'Tag'}</span>
                        {item.detected_side ? ` · ${item.detected_side}` : ''}
                        {statusLabel ? ` · ${statusLabel}` : ''}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Assign to roster member</span>
            <select
              className="input-field"
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              disabled={loadingMembers || sameSideMembers.length === 0}
              data-testid="override-member-select"
            >
              <option value="">
                {loadingMembers
                  ? 'Loading members…'
                  : sameSideMembers.length === 0
                    ? 'No same-side roster members'
                    : 'Select member'}
              </option>
              {sameSideMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {memberOptionLabel(member)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Note (optional)</span>
            <textarea
              className="input-field min-h-[72px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Commissioner confirmed this is the Cardinals owner"
              data-testid="override-note"
            />
          </label>

          <button
            type="button"
            className="btn-primary"
            disabled={!selectedMemberId || submitting}
            onClick={() => setConfirmOpen(true)}
            data-testid="override-submit-button"
          >
            Confirm identity override
          </button>
        </div>
      )}

      {confirmOpen && selectedTag && selectedMember && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="identity-override-modal-title"
          data-testid="identity-override-confirm-modal"
        >
          <div className="card max-w-md w-full relative">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="absolute top-4 right-4 text-secondary-400 hover:text-white"
              aria-label="Close"
              disabled={submitting}
            >
              <X className="h-5 w-5" />
            </button>
            <h2 id="identity-override-modal-title" className="text-lg font-semibold text-white mb-4">
              Confirm Identity Override
            </h2>
            <div className="text-sm text-secondary-300 space-y-4 mb-6">
              <div>
                <p className="text-secondary-400 mb-1">Detected HUD tag:</p>
                <p className="text-white font-medium" data-testid="confirm-override-raw-tag">
                  {selectedTag.raw_tag}
                </p>
              </div>
              <div>
                <p className="text-secondary-400 mb-1">Assign to:</p>
                <p className="text-white font-medium" data-testid="confirm-override-member">
                  {memberOptionLabel(selectedMember)}
                </p>
              </div>
              <p data-testid="confirm-override-attestation">
                This will mark the detected HUD tag as verified by commissioner review for this VOD
                only. It will not link this user&apos;s Twitch or Discord account and will not
                change their roster assignment.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void submitOverride()}
                disabled={submitting}
                data-testid="confirm-override-button"
              >
                {submitting ? 'Saving…' : 'Confirm override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionerIdentityOverridePanel;
